import { PuzzleMetadata, PuzzleType } from "sockapi/puzzle-data";
import { ScopeHooks } from "sockapi/scope-specs";
import { getTeamScopeIdComponents } from "sockapi/scope-specs-util";
import { ScopeType } from "sockapi/scope-types";
import { ScopeTeamIdentifier } from "sockapi/scopes";
import { SockAPIScopeState } from "sockapi/scope-state";
import { HintStrength, HintRequest } from "sockapi/scope-specs/hint-spec";

// WARNING: If monkeypatching this, make sure to update the client too
// as this is used to calculate the number of hints displayed in the UI.
export const HINT_CONFIG = {
  requestMaxLength: 10000,
  // prettier-ignore
  timedHintCounts: [
//  S     U     M     T     W     F     R
    0, 2, 0, 2, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
    0, 2, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    2, 0, 0, 0, 0, 0
  ],
  timedHintInterval: 12 * 60 * 60 * 1000,
};

export enum SubmitHintResultType {
  SUCCESS = "success",
  HUNT_IS_CLOSED = "hunt_is_closed",
  NO_HINT_TOKENS = "no_hint_tokens",
  OTHER_HINT_OPEN = "other_hint_open",
}

type SubmitHintResult =
  | {
      type:
        | SubmitHintResultType.SUCCESS
        | SubmitHintResultType.HUNT_IS_CLOSED
        | SubmitHintResultType.NO_HINT_TOKENS;
    }
  | {
      type: SubmitHintResultType.OTHER_HINT_OPEN;
      openHintPuzName: string;
    };

export type TeamHintsScopeSpec = {
  identifier: ScopeTeamIdentifier;
  state: {
    hints: { [puzName: string]: { [hintId: string]: true } };
    numGrantedTokens: number;
    /** Number of hint tokens used for each puzzle. */
    puzzleNumUsedTokens: { [puzName: string]: number };
    openHints: { [puzName: string]: true };
    categoryCounts?: { followup?: number } & {
      [category in HintStrength]?: number;
    };
  };
  update: {
    addHint?: {
      puzName: string;
      hintId: string;
    };
    setNumGrantedTokens?: number;
    updPuzzleNumUsedTokens?: { [puzName: string]: number };
    updOpenHints?: { [puzName: string]: boolean };
    addCount?: {
      isFollowup?: boolean;
      strength: HintStrength;
    };
  };
  step: {
    puzName: string;
    req: Omit<HintRequest, "timestamp">;
  };
  stepResponse: SubmitHintResult;
};

export const getNumTotalHintTokens = (
  isPosthunt: boolean,
  numGrantedTokens: number,
  {
    huntStartTime,
    huntEndTime,
  }: { huntStartTime: number; huntEndTime: number },
  now: number
): number => {
  if (isPosthunt || now >= huntEndTime) return 0;
  const numIntervals = Math.floor(
    Math.max(0, Math.min(now, huntEndTime) - huntStartTime) /
      HINT_CONFIG.timedHintInterval
  );
  let total = numGrantedTokens;
  for (let i = 0; i < numIntervals; ++i)
    total += HINT_CONFIG.timedHintCounts[i] ?? 0;
  return total;
};

export const getNumUsedHintTokensFromScopeState = (
  scopeState: SockAPIScopeState<ScopeType.TEAM_HINTS>
): number => {
  return Object.values(scopeState.puzzleNumUsedTokens).reduce(
    (acc, val) => acc + val,
    0
  );
};

export const isPuzzleEligibleForHint = (puzData: PuzzleMetadata): boolean => {
  return puzData.type === PuzzleType.PUZZLE;
};

export const checkSubmitHint = (
  isHuntClosed: boolean,
  isPosthunt: boolean,
  numUnusedHintTokens: number,
  openHintPuzName: string | null,
  isFollowup: boolean
): SubmitHintResult => {
  if (isHuntClosed) return { type: SubmitHintResultType.HUNT_IS_CLOSED };
  if (!isFollowup && numUnusedHintTokens <= 0)
    return { type: SubmitHintResultType.NO_HINT_TOKENS };
  if (openHintPuzName !== null)
    return {
      type: SubmitHintResultType.OTHER_HINT_OPEN,
      openHintPuzName,
    };
  return { type: SubmitHintResultType.SUCCESS };
};

export const teamHintsScopeHooks: ScopeHooks<ScopeType.TEAM_HINTS> = {
  identityFunc: (scope) => scope,
  getIdComponents: getTeamScopeIdComponents,
  update: (scopeState, payload) => {
    const {
      addHint,
      setNumGrantedTokens,
      updPuzzleNumUsedTokens,
      updOpenHints,
      addCount,
    } = payload;
    if (addHint !== undefined) {
      const { puzName, hintId } = addHint;
      scopeState.hints[puzName] = Object.assign(
        scopeState.hints[puzName] ?? {},
        { [hintId]: true }
      );
    }
    if (setNumGrantedTokens !== undefined)
      scopeState.numGrantedTokens = setNumGrantedTokens;
    for (const [puzName, numUsedTokens] of Object.entries(
      updPuzzleNumUsedTokens ?? {}
    )) {
      if (numUsedTokens === 0) delete scopeState.puzzleNumUsedTokens[puzName];
      else scopeState.puzzleNumUsedTokens[puzName] = numUsedTokens;
    }
    for (const [puzName, isOpen] of Object.entries(updOpenHints ?? {})) {
      if (isOpen) scopeState.openHints[puzName] = true;
      else delete scopeState.openHints[puzName];
    }
    if (addCount !== undefined) {
      const counts = (scopeState.categoryCounts ??= {});
      if (addCount.isFollowup) counts.followup = (counts.followup ?? 0) + 1;
      counts[addCount.strength] = (counts[addCount.strength] ?? 0) + 1;
    }
  },
};
